import React, { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import Layout from "../components/Layout";
import { CATEGORIES } from "../utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Plus, Pencil, Trash2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 10000;
const COLORS = ["#10b981", "#3b82f6", "#8b5cf6", "#f97316", "#ec4899", "#06b6d4", "#f59e0b", "#ef4444"];
const ALL_MODULES = [
  { key: "candidates", label: "Candidates" },
  { key: "clients", label: "Clients" },
  { key: "calendar", label: "Calendar" },
  { key: "kedatangan", label: "Kedatangan" },
  { key: "stock", label: "Stock (anyone can request, admins approve)" },
  { key: "jnj", label: "J&J (view only — supervisor login is separate)" },
  { key: "fitlah", label: "FitLah (wellness app — per device)" },
  { key: "permits", label: "Permits (visa & permit renewals)" },
  { key: "payouts", label: "Payouts (govt fees: levy, FOMEMA, etc.)" },
  { key: "accounting", label: "Accounting (daily in/out — owner-only delete)" },
  { key: "bills", label: "Bills & Payments" },
  { key: "tasks", label: "Tasks" },
  { key: "invoice", label: "Invoice (monthly workflow)" },
  { key: "vehicles", label: "Vehicles (fleet management)" },
  { key: "workers", label: "Workers Register (HR records)" },
  { key: "book", label: "Book" },
  { key: "analytics", label: "Analytics" },
  { key: "chat", label: "Chat (staff ↔ admin)" },
];

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([api.get("/staff"), api.get("/batch-requests")]);
      setStaff(s.data); setReqs(r.data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);

  const pending = reqs.filter((r) => r.status === "Pending");

  const review = async (id, status) => {
    try { await api.post(`/batch-requests/${id}/review`, { status }); toast.success(`Request ${status.toLowerCase()}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try { await api.delete(`/staff/${deleteTarget.id}`); toast.success("Staff removed"); setDeleteTarget(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/staff/${s.id}`, { is_active: !s.is_active });
      toast.success(`${s.display_name} is now ${!s.is_active ? "active" : "disabled"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const resetPenalty = async (s) => {
    try {
      await api.post(`/staff/${s.id}/reset_claims`);
      toast.success(`Claim penalty cleared for ${s.display_name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout title="Staff Management">
      <div className="space-y-6" data-testid="staff-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-xl font-semibold tracking-tight">Staff Management</h1>
          <Button onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-500" data-testid="add-staff-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add staff
          </Button>
        </div>

        <div className="rounded-lg border hairline surface-1 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium">Pending Batch Requests</div>
            <span className="text-xs num text-slate-500">{pending.length}</span>
          </div>
          {pending.length === 0 ? (
            <div className="text-xs text-slate-500 text-center py-6">No pending requests.</div>
          ) : (
            <div className="space-y-2">
              {pending.map((r) => (
                <div key={r.id} className="rounded-md border hairline surface-2 p-3 flex items-center gap-3 flex-wrap"
                  data-testid={`batch-req-${r.id}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: r.requested_by_color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm"><span className="font-medium">{r.requested_by_name}</span>
                      <span className="text-slate-400"> requested </span>
                      <span className="num">{r.quantity_requested}</span>
                      <span className="text-slate-400"> × </span>
                      <span>{r.category}</span>
                    </div>
                    {r.reason && <div className="text-xs text-slate-400 mt-0.5">"{r.reason}"</div>}
                  </div>
                  <Button size="sm" onClick={() => review(r.id, "Approved")}
                    className="bg-emerald-600 hover:bg-emerald-500" data-testid={`approve-${r.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => review(r.id, "Rejected")}
                    className="border-slate-700 text-slate-300" data-testid={`reject-${r.id}`}>
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border hairline surface-1 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b hairline">
                <th className="py-2.5 px-3">Name</th>
                <th className="px-3">Username</th>
                <th className="px-3">Role</th>
                <th className="px-3">Modules</th>
                <th className="px-3">Claim limit</th>
                <th className="px-3">Active</th>
                <th className="px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => {
                const mods = s.modules_allowed || [];
                const tabs = s.candidate_tabs_allowed || [];
                const isActive = s.is_active !== false;
                return (
                  <tr key={s.id} className="border-b border-slate-900/80" data-testid={`staff-row-${s.id}`}>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                        <span className="font-medium">{s.display_name}</span>
                      </div>
                    </td>
                    <td className="px-3 num text-slate-300">{s.username}</td>
                    <td className="px-3">
                      <span className={`text-xs num px-2 py-0.5 rounded-full border ${
                        s.role === "admin" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                          : "bg-slate-500/10 text-slate-300 border-slate-500/30"}`}>{s.role}</span>
                    </td>
                    <td className="px-3 text-xs text-slate-300">
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {mods.length === 0 && s.role !== "admin" && <span className="text-slate-600">none</span>}
                        {s.role === "admin" && <span className="text-emerald-400">all</span>}
                        {s.role !== "admin" && mods.map((m) => (
                          <span key={m} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border hairline surface-2">
                            {m === "candidates" && tabs.length > 0 && !tabs.includes("*")
                              ? `candidates (${tabs.length})`
                              : m}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 text-xs" data-testid={`claim-limit-${s.id}`}>
                      {s.role === "admin" ? (
                        <span className="text-slate-600">—</span>
                      ) : Number(s.max_claims_per_day || 0) > 0 ? (
                        <span className="num px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300">
                          Penalised · {s.max_claims_per_day}/day
                        </span>
                      ) : (
                        <span className="text-slate-500">Unlimited</span>
                      )}
                    </td>
                    <td className="px-3">
                      <Switch checked={isActive} onCheckedChange={() => toggleActive(s)}
                        data-testid={`toggle-active-${s.id}`} />
                    </td>
                    <td className="px-3 text-right">
                      {s.role !== "admin" && Number(s.max_claims_per_day || 0) > 0 && (
                        <Button size="sm" variant="ghost"
                          onClick={() => resetPenalty(s)}
                          className="text-amber-300 hover:text-amber-200 mr-1"
                          data-testid={`reset-penalty-${s.id}`}
                          title="Clear appointment-claim penalty">
                          <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Reset
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setEditTarget(s)}
                        className="text-slate-300" data-testid={`edit-staff-${s.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      {s.role !== "admin" && (
                        <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(s)}
                          className="text-red-400 hover:text-red-300" data-testid={`delete-staff-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <StaffDialog open={addOpen} onOpenChange={setAddOpen} onDone={load} />
        <StaffDialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)} onDone={load} target={editTarget} />

        <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
          <AlertDialogContent className="bg-[#0f1626] border-slate-800 text-slate-100">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {deleteTarget?.display_name}?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                This revokes their access immediately. Their claimed candidates remain assigned to them; reassign before deletion if needed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-700">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-500" data-testid="confirm-delete-staff">Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

function StaffDialog({ open, onOpenChange, onDone, target }) {
  const empty = {
    username: "", password: "", display_name: "", role: "staff", color: "#3b82f6",
    phone: "",
    modules: [], tabs_mode: "all", categories: [],
    roster_mode: "selected", roster_companies: [],
    bills_can_add: false, bills_can_verify: false, bills_can_pay: false,
    invoice_clients: [],
    invoice_retriever: false,
    roster_photo_uploader: false,
    wa_notify_enabled: true,
  };
  const [form, setForm] = useState(empty);
  const [allCompanies, setAllCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState("");
  const pickerRef = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/roster/companies/all").then(({ data }) => setAllCompanies(data || [])).catch(() => {});
  }, [open]);

  // When editing a staff who already has Kedatangan enabled, scroll the dialog
  // to the company picker so the admin sees the assignable list right away.
  useEffect(() => {
    if (!open || !target) return;
    if (!(target.modules_allowed || []).includes("kedatangan")) return;
    const t = setTimeout(() => {
      pickerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 350);
    return () => clearTimeout(t);
  }, [open, target]);

  useEffect(() => {
    if (target) {
      const tabs = target.candidate_tabs_allowed || [];
      const tabsMode = tabs.includes("*") || tabs.length === 0 ? "all" : "selected";
      const rosterIds = target.roster_companies_allowed || [];
      setForm({
        username: target.username, password: "",
        display_name: target.display_name, role: target.role, color: target.color,
        phone: target.phone || "",
        modules: target.modules_allowed || [],
        tabs_mode: tabsMode,
        categories: tabsMode === "all" ? [] : tabs,
        roster_mode: "selected",
        roster_companies: rosterIds,
        bills_can_add: !!target.bills_can_add,
        bills_can_verify: !!target.bills_can_verify,
        bills_can_pay: !!target.bills_can_pay,
        invoice_clients: target.invoice_clients_allowed || [],
        invoice_retriever: !!target.invoice_retriever,
        roster_photo_uploader: !!target.roster_photo_uploader,
        wa_notify_enabled: target.wa_notify_enabled !== false,
      });
    } else {
      setForm(empty);
    }
  }, [target, open]);  // eslint-disable-line

  const toggleModule = (key) => {
    const has = form.modules.includes(key);
    setForm({ ...form, modules: has ? form.modules.filter((m) => m !== key) : [...form.modules, key] });
  };
  const toggleCat = (c) => {
    const has = form.categories.includes(c);
    setForm({ ...form, categories: has ? form.categories.filter((x) => x !== c) : [...form.categories, c] });
  };
  const toggleCompany = (id) => {
    const has = form.roster_companies.includes(id);
    setForm({ ...form, roster_companies: has
      ? form.roster_companies.filter((x) => x !== id)
      : [...form.roster_companies, id] });
  };
  const setAllCompaniesSelected = () => setForm({ ...form, roster_companies: allCompanies.map((c) => c.id) });
  const clearAllCompanies = () => setForm({ ...form, roster_companies: [] });

  const submit = async () => {
    try {
      const candidate_tabs_allowed = form.modules.includes("candidates")
        ? (form.tabs_mode === "all" ? ["*"] : form.categories)
        : [];
      const roster_companies_allowed = form.modules.includes("kedatangan")
        ? form.roster_companies : [];
      if (target) {
        const body = {
          display_name: form.display_name, color: form.color,
          phone: form.phone || "",
          modules_allowed: form.modules, candidate_tabs_allowed, role: form.role,
          roster_companies_allowed,
          bills_can_add: !!form.bills_can_add,
          bills_can_verify: !!form.bills_can_verify,
          bills_can_pay: !!form.bills_can_pay,
          invoice_clients_allowed: form.modules.includes("invoice")
            ? form.invoice_clients : [],
          invoice_retriever: !!form.invoice_retriever,
          roster_photo_uploader: !!form.roster_photo_uploader,
          wa_notify_enabled: !!form.wa_notify_enabled,
        };
        if (form.password) body.password = form.password;
        await api.put(`/staff/${target.id}`, body);
        toast.success("Staff updated");
      } else {
        if (!form.username || !form.password || !form.display_name) {
          toast.error("Username, password, display name required");
          return;
        }
        await api.post("/staff", {
          username: form.username, password: form.password, display_name: form.display_name,
          role: form.role, color: form.color,
          phone: form.phone || "",
          modules_allowed: form.modules, candidate_tabs_allowed,
          roster_companies_allowed,
          bills_can_add: !!form.bills_can_add,
          bills_can_verify: !!form.bills_can_verify,
          bills_can_pay: !!form.bills_can_pay,
          invoice_clients_allowed: form.modules.includes("invoice")
            ? form.invoice_clients : [],
          invoice_retriever: !!form.invoice_retriever,
          roster_photo_uploader: !!form.roster_photo_uploader,
          wa_notify_enabled: !!form.wa_notify_enabled,
        });
        toast.success("Staff created");
      }
      onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filteredCompanies = allCompanies.filter((c) =>
    !companySearch || c.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="staff-dialog">
        <DialogHeader><DialogTitle>{target ? "Edit staff" : "Add staff"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-400">Display name</Label>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                className="bg-[#0a0e1a] border-slate-800 mt-1" /></div>
            <div><Label className="text-xs text-slate-400">Username</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                disabled={!!target} className="bg-[#0a0e1a] border-slate-800 mt-1" /></div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">{target ? "New password (leave blank to keep current)" : "Password"}</Label>
            <Input type="password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">WhatsApp phone (optional · used for claim auto-DMs)</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="e.g. +60123456789"
              className="bg-[#0a0e1a] border-slate-800 mt-1 num" data-testid="staff-phone" />
            <label className="mt-2.5 flex items-center gap-2 cursor-pointer select-none text-xs">
              <Checkbox
                checked={!!form.wa_notify_enabled}
                onCheckedChange={() => setForm({ ...form, wa_notify_enabled: !form.wa_notify_enabled })}
                data-testid="staff-wa-notify-toggle"
              />
              <span className="text-slate-300">
                Receive WhatsApp notifications for new chats & tasks
              </span>
            </label>
            <p className="mt-1 text-[11px] text-slate-500 leading-snug">
              Each staff must first text <code className="text-amber-300 num">join &lt;your-code&gt;</code> to
              <span className="num text-slate-300"> +1 415 523 8886</span> to opt-in to the Twilio sandbox.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Colour</Label>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                    className={`w-6 h-6 rounded-full border-2 transition-all ${form.color === c ? "border-white scale-110" : "border-transparent"}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm text-slate-200">Module permissions</Label>
            <div className="text-xs text-slate-500 mb-2">Admins see every module regardless; these apply to staff accounts.</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 rounded-md border hairline surface-2">
              {ALL_MODULES.map((m) => (
                <label key={m.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`perm-${m.key}`}>
                  <Checkbox checked={form.modules.includes(m.key)} onCheckedChange={() => toggleModule(m.key)} />
                  <span className="flex items-center gap-1.5">
                    {m.label}
                    {m.key === "kedatangan" && form.modules.includes("kedatangan") && (
                      <span className="text-[10px] num px-1.5 py-0.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300">
                        {form.roster_companies.length}/{allCompanies.length || 19}
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {form.modules.includes("candidates") && (
            <div className="rounded-md border border-blue-500/20 p-3 bg-blue-500/5">
              <Label className="text-sm text-slate-200">Candidate tab access</Label>
              <Select value={form.tabs_mode} onValueChange={(v) => setForm({ ...form, tabs_mode: v })}>
                <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  <SelectItem value="selected">Selected categories only</SelectItem>
                </SelectContent>
              </Select>
              {form.tabs_mode === "selected" && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {CATEGORIES.map((c) => (
                    <label key={c} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.categories.includes(c)} onCheckedChange={() => toggleCat(c)} />
                      {c}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {form.modules.includes("kedatangan") && (
            <div ref={pickerRef} className="rounded-md border border-violet-500/20 p-3 bg-violet-500/5"
              data-testid="roster-company-picker">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-sm text-slate-200">Kedatangan · company access</Label>
                <div className="flex items-center gap-1">
                  <Button type="button" size="sm" variant="ghost"
                    className="text-xs text-slate-400 hover:text-slate-200"
                    onClick={setAllCompaniesSelected}>Select all</Button>
                  <Button type="button" size="sm" variant="ghost"
                    className="text-xs text-slate-400 hover:text-slate-200"
                    onClick={clearAllCompanies}>Clear</Button>
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                This staff will only see rosters for the ticked companies.
                {" "}{form.roster_companies.length}/{allCompanies.length} selected.
              </div>
              <Input
                placeholder="Filter companies…"
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                className="bg-[#0a0e1a] border-slate-800 mt-3 h-8 text-sm"
                data-testid="roster-company-search"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-2 max-h-[240px] overflow-y-auto pr-1">
                {filteredCompanies.length === 0 && (
                  <div className="text-xs text-slate-500 col-span-2 py-4 text-center">No companies.</div>
                )}
                {filteredCompanies.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer px-1.5 py-1 rounded hover:bg-white/5"
                    data-testid={`roster-company-${c.name.toLowerCase().replace(/\s+/g,'-')}`}>
                    <Checkbox checked={form.roster_companies.includes(c.id)}
                      onCheckedChange={() => toggleCompany(c.id)} />
                    <span className="truncate">{c.name}</span>
                  </label>
                ))}
              </div>

              <div className="mt-3 pt-3 border-t border-violet-500/20 text-[11px] text-slate-400 leading-relaxed">
                <span className="text-slate-300 font-medium">Worker reference photos:</span> any
                staff with Kedatangan access can upload or replace a worker photo.
                Only admin can delete photos (after verification).
              </div>
            </div>
          )}

          {form.modules.includes("bills") && (
            <div className="rounded-md border border-orange-500/20 p-3 bg-orange-500/5"
              data-testid="bills-permissions">
              <Label className="text-sm text-slate-200">Bills & Payments · permissions</Label>
              <div className="text-xs text-slate-500 mt-1 mb-3">
                Admin always has full access. For staff, tick only what this person may do.
              </div>
              <div className="grid grid-cols-1 gap-2">
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="bills-can-add">
                  <Checkbox checked={form.bills_can_add}
                    onCheckedChange={() => setForm({ ...form, bills_can_add: !form.bills_can_add })} />
                  <div>
                    <div>Can <span className="text-slate-200">add / edit</span> bills & upload bill documents</div>
                    <div className="text-[11px] text-slate-500">Enter new bills — e.g. Desmond</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="bills-can-verify">
                  <Checkbox checked={form.bills_can_verify}
                    onCheckedChange={() => setForm({ ...form, bills_can_verify: !form.bills_can_verify })} />
                  <div>
                    <div>Can <span className="text-slate-200">verify</span> bills</div>
                    <div className="text-[11px] text-slate-500">Moves a bill from Pending to Verified</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="bills-can-pay">
                  <Checkbox checked={form.bills_can_pay}
                    onCheckedChange={() => setForm({ ...form, bills_can_pay: !form.bills_can_pay })} />
                  <div>
                    <div>Can <span className="text-slate-200">record payments</span> & see payment details</div>
                    <div className="text-[11px] text-slate-500">Users without this permission cannot see paid amounts, methods, or receipts</div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {form.modules.includes("invoice") && form.role !== "admin" && (
            <div className="rounded-md border border-yellow-500/20 p-3 bg-yellow-500/5"
              data-testid="invoice-permissions">
              <Label className="text-sm text-slate-200">Invoice · client access</Label>
              <div className="text-xs text-slate-500 mt-1 mb-3">
                Tick the invoice clients this staff can upload timesheets for and retrieve packs from.
                Admin always has access to every client.
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer mb-3" data-testid="invoice-retriever">
                <Checkbox checked={form.invoice_retriever}
                  onCheckedChange={() => setForm({ ...form, invoice_retriever: !form.invoice_retriever })} />
                <div>
                  <div>Can <span className="text-slate-200">retrieve all invoices</span> across every client</div>
                  <div className="text-[11px] text-slate-500">Grants read + retrieve access everywhere. Still cannot upload timesheets for unmapped clients.</div>
                </div>
              </label>
              <InvoiceClientPicker
                allCompanies={allCompanies}
                selected={form.invoice_clients}
                onChange={(ids) => setForm({ ...form, invoice_clients: ids })}
              />
            </div>
          )}

        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-blue-600 hover:bg-blue-500" data-testid="staff-submit">
            {target ? "Save changes" : "Create staff"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceClientPicker({ allCompanies, selected, onChange }) {
  const [q, setQ] = React.useState("");
  const toggle = (id) => {
    const has = selected.includes(id);
    onChange(has ? selected.filter((x) => x !== id) : [...selected, id]);
  };
  const setAll = () => onChange((allCompanies || []).filter((c) => c.is_active !== false).map((c) => c.id));
  const clearAll = () => onChange([]);
  const filtered = (allCompanies || [])
    .filter((c) => c.is_active !== false)
    .filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-2" data-testid="invoice-client-picker">
      <div className="flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter clients…"
          className="bg-[#0a0e1a] border-slate-800 h-8 text-xs"
          data-testid="invoice-client-search" />
        <Button type="button" size="sm" variant="ghost" className="h-8 text-xs"
          onClick={setAll}>Select all</Button>
        <Button type="button" size="sm" variant="ghost" className="h-8 text-xs"
          onClick={clearAll}>Clear</Button>
      </div>
      <div className="max-h-[180px] overflow-y-auto rounded border hairline grid grid-cols-1 sm:grid-cols-2 gap-1 p-2">
        {filtered.length === 0 && (
          <div className="text-xs text-slate-500 text-center py-3 col-span-full">No clients match.</div>
        )}
        {filtered.map((c) => (
          <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer px-1 py-0.5 rounded hover:bg-white/5"
            data-testid={`invoice-client-${c.id}`}>
            <Checkbox checked={selected.includes(c.id)}
              onCheckedChange={() => toggle(c.id)} />
            <span className="truncate">{c.name}</span>
          </label>
        ))}
      </div>
      <div className="text-[11px] text-slate-500 num">{selected.length} selected</div>
    </div>
  );
}
